import { useState, type ReactElement } from "react";
import type { ISummaryBox } from "@app/dashboard/types/innerLayout/cards/summary-box";
import type { ILabelValueObject } from "@app/dashboard/types/innerLayout/cards";
import { ChevronDown, ChevronUp } from "@unified-checkout/design-system/icons/index";
import { FeaturedIcon, FormatCurrencyForDisplay } from "@unified-checkout/design-system";
import { Card } from "../CardWrapper";
import styles from "./summary-box.module.scss";

const getText = (item: string | ILabelValueObject): string => {
  return typeof item === 'string' ? item : item.text;
};

const getHintText = (item: string | ILabelValueObject): string | undefined => {
  return typeof item === 'string' ? undefined : item.hintText;
};

const getLeadingIcon = (item: string | ILabelValueObject): ReactElement<'svg'> | string | undefined => {
  return typeof item === 'string' ? undefined : item.leading_icon;
};

export const SummaryBox = ({
  width,
  height,
  title,
  itemsList,
  isNested = false,
}: ISummaryBox) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleItem = (index: number) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(index)) {
      newExpandedItems.delete(index);
    } else {
      newExpandedItems.add(index);
    }
    setExpandedItems(newExpandedItems);
  };

  return (
    <Card
      width={width}
      height={height}
      title={title}
    >
      <div
        className={styles["summary-box"]}
        data-summary-box-nested={isNested}
      >
        {
          itemsList?.map((item, index) =>
            <div
              key={`summary-box-item-${index}`}
              className={styles["summary-box__item"]}
              data-summary-box-collapsible={!!item.isCollapsible}
              data-summary-box-item-main={!!item.isMain}
            >
              {
                item.label &&
                <div
                  className={styles["summary-box__item__label"]}
                  onClick={item.isCollapsible ? () => toggleItem(index) : undefined}
                >
                  <div className={styles["summary-box__item__label__content"]}>
                    {getLeadingIcon(item.label) && (
                      <div className={styles["summary-box__item__label__content__leading-icon"]}>
                        {
                          typeof getLeadingIcon(item.label) === 'string' ?
                            <img src={getLeadingIcon(item.label) as string} />
                            :
                            getLeadingIcon(item.label)
                        }
                      </div>
                    )}
                    <div>
                      {FormatCurrencyForDisplay(getText(item.label))}
                      {
                        getHintText(item.label) &&
                        <p className={styles["summary-box__item__label__content__hint-text"]}>
                          {getHintText(item.label)}
                        </p>
                      }
                    </div>
                  </div>
                  {
                    item.isCollapsible && (
                      <FeaturedIcon
                        size="sm"
                        color="gray"
                        variant="outlined"
                        keepIconDefaultColors={true}
                        icon={expandedItems.has(index) ? ChevronDown : ChevronUp}
                      />
                    )
                  }
                </div>
              }
              {
                item.value && !item.isCollapsible &&
                  <div
                    className={styles["summary-box__item__value"]}>
                    {
                      getLeadingIcon(item.value) &&
                      <div className={styles["summary-box__item__value__leading-icon"]}>
                        {
                          typeof getLeadingIcon(item.value) === 'string' ?
                            <img src={getLeadingIcon(item.value) as string} />
                            :
                            getLeadingIcon(item.value)
                        }
                      </div>
                    }
                    <div>
                      {typeof item.value === 'string' ? FormatCurrencyForDisplay(getText(item.value)) : getText(item.value)}
                      {
                        getHintText(item.value) &&
                        <p className={styles["summary-box__item__value__hint-text"]}>
                          {FormatCurrencyForDisplay(getHintText(item.value) as string)}
                        </p>
                      }
                    </div>
                  </div>     
              }
              {
                item.isCollapsible && expandedItems.has(index) && item.collapsibleItems && (
                  <div className={styles["summary-box__item__collapsible"]}>
                    {
                      item.collapsibleItems.map((collapsibleItem, collapsibleIndex) => (
                        <div
                          key={`collapsible-item-${collapsibleIndex}`}
                          className={styles["summary-box__item__collapsible__sub-item"]}
                        >
                          <div className={styles["summary-box__item__collapsible__sub-item__label"]}>
                            {getText(collapsibleItem.label)}
                            {
                              getHintText(collapsibleItem.label) &&
                              <p className={styles["summary-box__item__collapsible__sub-item__label__hint-text"]}>
                                {getHintText(collapsibleItem.label)}
                              </p>
                            }
                          </div>
                          <div className={styles["summary-box__item__collapsible__sub-item__value"]}>
                            {collapsibleItem.value && FormatCurrencyForDisplay(getText(collapsibleItem.value))}
                            {
                              collapsibleItem.value && getHintText(collapsibleItem.value) &&
                              <p className={styles["summary-box__item__collapsible__sub-item__value__hint-text"]}>
                                {getHintText(collapsibleItem.value)}
                              </p>
                            }
                          </div>
                        </div>
                      ))
                    }
                  </div>
                )
              }
            </div>
          )
        }
      </div>
    </Card>
  )
}
